import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { ErrorMessage } from 'secondstep-components'
import {
  operations as licenseOperations,
  selectors as licenseSelectors,
} from 'store/licenseManager'
import { operations as loadingOperations } from 'store/loadingManager'
import {
  operations as reportsOperations,
  selectors as reportsSelectors,
} from 'store/reportsManager'
import { K8_GRADE_KEY_TO_GRAPH_ENUM } from 'store/reportsManager/constants'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import { navigationEvent } from 'layers/navigation/store/operations'
import NavigationHelper from 'layers/navigation/navigationHelper'
import { REPLACE } from 'layers/navigation/store/types'
import K8Report from './component'
import {
  DEFAULT_K8_REPORT_UNIT_SECTION,
  ENTRY_ID,
  getK8ReportFlagOnRedirectTarget,
  isK8ReportUnitSection,
  K8_GRADE_NAME_TO_GRADE_SLUG_MAP,
} from './constants'
import {
  ACTIVITY_NAME_TO_SLUG_MAP,
  GRADE_NAME_TO_GRADE_SLUG_MAP,
  UNIT_RANGE_NAME_TO_SLUG_MAP,
} from './nameToSlugMap'

export const K8ReportContainer = ({
  activeAdminK8Sites,
  classDetailsShouldFetch,
  classInstance,
  entry,
  errorStatusCode,
  fetchK8ReportClassDetails,
  fetchK8ReportGrade,
  fetchK8ReportGradeAndClassDetails,
  fetchK8ReportSchool,
  fetchUserSites,
  gradeAndClassShouldFetch,
  gradeEmptyDisplay,
  gradeGraphEnum,
  gradeShouldFetch,
  isFetchingUserSites,
  isLoadingApp,
  location,
  match,
  navigationEvent,
  query,
  schoolEmptyDisplay,
  schoolShouldFetch,
  selectedSite,
  setAppIsLoadingState,
  setAppNotLoadingState,
  shouldLoad,
  k8ReportUnitSection,
  pathnameNeedsRedirect,
  redirectTargetPath,
  updateSelectedProgram,
  updateSelectedSite,
}) => {
  useEffect(() => {
    updateSelectedProgram({ name: 'K8', key: 'K8' })
  }, [])

  useEffect(() => {
    if (shouldLoad && isLoadingApp) setAppIsLoadingState('Loading content...')
    if (!shouldLoad && !isLoadingApp) setAppNotLoadingState()
  }, [isLoadingApp, shouldLoad])

  useEffect(() => {
    if (!pathnameNeedsRedirect || !redirectTargetPath) {
      return
    }
    const queryParamsByKey = {
      ...(query?.site && { site: query.site }),
      ...(query?.class && { class: query.class }),
    }
    const url = NavigationHelper.generateRouteWithQueryParams(
      redirectTargetPath,
      queryParamsByKey,
    )
    navigationEvent(url, REPLACE)
  }, [
    pathnameNeedsRedirect,
    redirectTargetPath,
    navigationEvent,
    query?.site,
    query?.class,
  ])

  useEffect(() => {
    if (!activeAdminK8Sites) {
      fetchUserSites()
    }
    if (schoolShouldFetch) {
      fetchK8ReportSchool(selectedSite)
    }
    if (gradeAndClassShouldFetch) {
      fetchK8ReportGradeAndClassDetails(
        selectedSite,
        gradeGraphEnum,
        classInstance,
      )
    } else {
      if (gradeShouldFetch) {
        fetchK8ReportGrade(selectedSite, gradeGraphEnum)
      }
      if (classDetailsShouldFetch) {
        fetchK8ReportClassDetails(selectedSite, classInstance)
      }
    }
  }, [
    classDetailsShouldFetch,
    classInstance,
    errorStatusCode,
    gradeAndClassShouldFetch,
    gradeGraphEnum,
    gradeShouldFetch,
    schoolShouldFetch,
    selectedSite,
  ])

  useEffect(() => {
    if (activeAdminK8Sites?.length) {
      if (!query.site && location.pathname.includes('/reports/k8')) {
        navigationHandler(
          {
            siteId: activeAdminK8Sites[0].id,
            ...(query?.class && { class: query.class }),
          },
          REPLACE,
        )
      } else if (!selectedSite || query.site !== selectedSite.toString()) {
        const matchingSite =
          activeAdminK8Sites.find(site => site.id.toString() === query.site) ||
          {}
        updateSelectedSite(matchingSite.id)
      }
    }
  }, [
    activeAdminK8Sites,
    isFetchingUserSites,
    query.class,
    query.site,
    selectedSite,
  ])

  const navigationHandler = (paramsByKey, type) => {
    const {
      activity,
      classInstance,
      gradeName,
      grades,
      siteId,
      unitSection,
      unit,
    } = paramsByKey
    const gradesSlugs = grades?.map(
      grade => GRADE_NAME_TO_GRADE_SLUG_MAP[grade],
    )
    const activitySlugs = activity?.map(
      activity => ACTIVITY_NAME_TO_SLUG_MAP[activity],
    )
    const unitSlug = UNIT_RANGE_NAME_TO_SLUG_MAP?.[unit] || ''
    const unitSegment =
      unitSection || k8ReportUnitSection || DEFAULT_K8_REPORT_UNIT_SECTION
    const queryParamsByKey = {
      ...(siteId && { site: siteId }),
      ...(classInstance && { class: classInstance }),
      ...(grades?.length > 0 && { grades: gradesSlugs.join(',') }),
      ...(activity?.length > 0 && { activity: activitySlugs.join(',') }),
      ...(unit && { unit: unitSlug }),
    }
    const gradeSlug = K8_GRADE_NAME_TO_GRADE_SLUG_MAP[gradeName] || ''
    const gradeParam = gradeSlug ? `/${gradeSlug}/` : ''
    const route = `/reports/k8/${unitSegment}${gradeParam}`
    const url = NavigationHelper.generateRouteWithQueryParams(
      route,
      queryParamsByKey,
    )

    return navigationEvent(url, type)
  }

  const isSettled =
    !isFetchingUserSites && activeAdminK8Sites?.length > 0 && !!entry
  if (!isSettled) {
    return null
  }
  if (!selectedSite) {
    return (
      <ErrorMessage
        additionalDetails={`Don't have access to view site with id: ${query.site}. If you believe you should have access to this site, please contact us.`}
        serviceName={'accessError'}
        statusCode={401}
      />
    )
  }

  if (pathnameNeedsRedirect) {
    return null
  }

  return (
    <K8Report
      entry={entry}
      gradeEmptyDisplay={gradeEmptyDisplay}
      match={match}
      navigationEvent={navigationEvent}
      navigationHandler={navigationHandler}
      schoolEmptyDisplay={schoolEmptyDisplay}
    />
  )
}

const mapStateToProps = (state, ownProps) => {
  const { licenseManager, loadingManager, reportsManager, router } = state
  const activeAdminK8Sites = licenseSelectors.selectActiveAdminK8Sites(state)
  const { isFetchingUserSites } = licenseManager || {}
  const { error, k8Reports, selectedSite } = reportsManager || {}
  const { status: errorStatusCode } = error || {}
  const { isLoading: isLoadingApp } = loadingManager
  const shouldLoad =
    (!activeAdminK8Sites || !ownProps.entry || !selectedSite) &&
    !errorStatusCode
  const { location } = router
  const { pathname, query } = router.location
  const { class: classInstance } = query || {}
  const parts = pathname?.split('/').filter(Boolean) || []
  const thirdSeg = parts[2]
  const fourthSeg = parts[3]
  const unitSegment = isK8ReportUnitSection(thirdSeg) ? thirdSeg : null
  const gradeSlugSegment =
    unitSegment && fourthSeg
      ? fourthSeg
      : !unitSegment && thirdSeg?.startsWith('grade-')
      ? thirdSeg
      : null
  const gradeKey = gradeSlugSegment?.split('-')[1]
  const gradeGraphEnum = K8_GRADE_KEY_TO_GRAPH_ENUM[gradeKey]

  const redirectTargetPath = getK8ReportFlagOnRedirectTarget(pathname)
  const pathnameNeedsRedirect = !!redirectTargetPath

  const k8ReportUnitSection = unitSegment || DEFAULT_K8_REPORT_UNIT_SECTION

  const isSchoolPage =
    !!unitSegment &&
    parts.length === 3 &&
    !query?.class &&
    !!query?.site &&
    !errorStatusCode
  const isGradePage =
    !!unitSegment && !!fourthSeg?.includes('grade') && !query.class
  const isClassPage = query.class
  const schoolSummaryReport = reportsSelectors.selectSchoolSummaryReport(state)
  const gradeReport = reportsSelectors.selectGradeReport(state)
  const classDetailsReport = reportsSelectors.selectClassDetailsReport(
    state,
    classInstance,
  )
  const schoolShouldFetch =
    !pathnameNeedsRedirect &&
    isSchoolPage &&
    !!selectedSite &&
    !schoolSummaryReport
  const gradeShouldFetch =
    !pathnameNeedsRedirect &&
    isGradePage &&
    !isClassPage &&
    selectedSite &&
    !gradeReport
  const classDetailsShouldFetch =
    !pathnameNeedsRedirect &&
    isClassPage &&
    selectedSite &&
    classInstance &&
    (!classDetailsReport || !activeAdminK8Sites || !ownProps.entry) &&
    !errorStatusCode
  const gradeAndClassShouldFetch =
    !pathnameNeedsRedirect &&
    pathname?.includes('grade') &&
    query.class &&
    !gradeReport &&
    selectedSite &&
    (!classDetailsReport || !activeAdminK8Sites || !ownProps.entry) &&
    !errorStatusCode

  return {
    activeAdminK8Sites,
    classDetailsShouldFetch,
    classInstance,
    errorStatusCode,
    gradeAndClassShouldFetch,
    gradeGraphEnum,
    gradeShouldFetch,
    k8ReportUnitSection,
    k8Reports,
    isFetchingUserSites,
    isLoadingApp,
    location,
    pathnameNeedsRedirect,
    query,
    redirectTargetPath,
    schoolShouldFetch,
    selectedSite,
    shouldLoad,
  }
}

const mapDispatchToProps = {
  fetchK8ReportClassDetails:
    reportsOperations.fetchK8ReportClassDetailsBySiteId,
  fetchK8ReportGrade: reportsOperations.fetchK8ReportGradeBySiteId,
  fetchK8ReportGradeAndClassDetails:
    reportsOperations.fetchK8ReportGradeAndClassDetailsBySiteId,
  fetchK8ReportSchool: reportsOperations.fetchK8ReportSchoolBySiteId,
  fetchUserSites: licenseOperations.fetchUserSites,
  navigationEvent,
  setAppIsLoadingState: loadingOperations.setIsLoadingState,
  setAppNotLoadingState: loadingOperations.setNotLoadingState,
  updateSelectedSite: reportsOperations.updateSelectedSite,
  updateSelectedProgram: reportsOperations.updateSelectedProgram,
}

const mapper = entry => {
  if (!entry) {
    return {}
  }
  const {
    gradeEmptyStateIcon,
    gradeEmptyStateText,
    gradeEmptyStateHeader,
    schoolEmptyStateIcon,
    schoolEmptyStateText,
    schoolEmptyStateHeader,
  } = entry
  return {
    entry,
    gradeEmptyDisplay: {
      header: gradeEmptyStateHeader,
      icon: gradeEmptyStateIcon,
      text: gradeEmptyStateText,
    },
    schoolEmptyDisplay: {
      header: schoolEmptyStateHeader,
      icon: schoolEmptyStateIcon,
      text: schoolEmptyStateText,
    },
  }
}

K8ReportContainer.propTypes = {
  activeAdminK8Sites: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  classDetailsShouldFetch: PropTypes.bool,
  classInstance: PropTypes.string,
  entry: PropTypes.object,
  errorStatusCode: PropTypes.number,
  fetchK8ReportClassDetails: PropTypes.func,
  fetchK8ReportGrade: PropTypes.func,
  fetchK8ReportGradeAndClassDetails: PropTypes.func,
  fetchK8ReportSchool: PropTypes.func,
  fetchUserSites: PropTypes.func,
  getIdFromQueryParams: PropTypes.func,
  gradeAndClassShouldFetch: PropTypes.bool,
  gradeEmptyDisplay: PropTypes.object,
  gradeGraphEnum: PropTypes.string,
  gradeShouldFetch: PropTypes.bool,
  isFetchingUserSites: PropTypes.bool,
  isLoadingApp: PropTypes.bool,
  k8ReportUnitSection: PropTypes.string,
  location: PropTypes.object,
  match: PropTypes.object,
  navigationEvent: PropTypes.func,
  pathnameNeedsRedirect: PropTypes.bool,
  query: PropTypes.object,
  redirectTargetPath: PropTypes.string,
  schoolEmptyDisplay: PropTypes.object,
  schoolShouldFetch: PropTypes.bool,
  selectedSite: PropTypes.number,
  setAppIsLoadingState: PropTypes.func,
  setAppNotLoadingState: PropTypes.func,
  shouldLoad: PropTypes.bool,
  updateSelectedProgram: PropTypes.func,
  updateSelectedSite: PropTypes.func,
}

export default ContentEntryDataGetter(
  K8ReportContainer,
  {
    entryId: ENTRY_ID,
    include: 3,
    mapper,
    spread: true,
  },
  connect(mapStateToProps, mapDispatchToProps),
)
